import React, { useState } from "react";
import { Loader2 } from "lucide-react";

interface DownloadLinkProps {
  url: string;
  text: string;
  saveToFileSystem?: boolean;
}

export const DownloadLink: React.FC<DownloadLinkProps> = ({ url, text, saveToFileSystem = false }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const filename = decodeURIComponent(
        url.split("/").pop()?.split("?")[0] || "download"
      );

      // use File System Access API if available
      if ("showSaveFilePicker" in window && saveToFileSystem) {
        // infer extension from filename
        const ext = filename.includes(".")
          ? filename.slice(filename.lastIndexOf("."))
          : "";

        // @ts-ignore
        const handle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [
            {
              description: "Downloaded file",
              accept: { "application/octet-stream": [ext || ".bin"] },
            },
          ],
        });

        const writable = await handle.createWritable();
        const res = await fetch(url);
        if (!res.ok || !res.body) throw new Error("Download failed");

        await res.body.pipeTo(writable);
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error("Download failed");
  
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const filename =
          url.split("/").pop()?.split("?")[0] || "downloaded-file";
  
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <span>
      <a
        href={url}
        onClick={handleClick}
        data-testid="chat-download-link"
        className="inline-flex cursor-pointer items-center gap-1.5"
      >
        {text}
        {loading && (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden />
        )}
      </a>
    </span>
  );
};